if (!customElements.get('products-card')) {
  customElements.define(
    'products-card',
    class ProductsCard extends HTMLElement {
      constructor() {
        super();
        this.tabs = Array.from(this.querySelectorAll('.suunto-products-card__tab'));
        this.panels = Array.from(this.querySelectorAll('.suunto-products-card__panel'));
        this.productSwiperEls = Array.from(this.querySelectorAll('.suunto-products-card__product-swiper'));
        this.actionsDesktop = this.querySelector('.suunto-products-card__actions--desktop');
        this.readyProductSwipers = new WeakSet();
        this.pendingProductSwipers = new WeakSet();
        this.init();
      }

      init() {
        if (!this.tabs.length) return;

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => {
            this.setActiveTab(index);
          });
          tab.addEventListener('keydown', (event) => {
            this.onTabKeydown(event, index);
          });
        });

        this.initSheet();
        this.setActiveTab(0, false);
        this.initDeferredSwiper();
      }

      initDeferredSwiper() {
        const activate = () => {
          this.swiperObserver?.disconnect();
          this.activateProductSwiper(this.activeTabIndex, false);
        };

        if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
          activate();
          return;
        }

        this.swiperObserver = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            activate();
          },
          { rootMargin: '300px 0px' }
        );
        this.swiperObserver.observe(this);
      }

      setActiveTab(index, initializeSwiper = true) {
        if (!this.tabs[index] || !this.panels[index]) return;

        const resetSwiper = index !== this.activeTabIndex;
        this.activeTabIndex = index;

        this.tabs.forEach((tab, tabIndex) => {
          const isActive = tabIndex === index;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', String(isActive));
          tab.tabIndex = isActive ? 0 : -1;
        });

        this.panels.forEach((panel, panelIndex) => {
          panel.hidden = panelIndex !== index;
        });

        if (initializeSwiper) {
          this.swiperObserver?.disconnect();
          this.activateProductSwiper(index, resetSwiper);
        }
        this.updateActions(this.tabs[index]);
      }

      onTabKeydown(event, index) {
        let nextIndex;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (index + 1) % this.tabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = this.tabs.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        this.tabs[nextIndex].focus();
        this.setActiveTab(nextIndex);
      }

      updateActions(tab) {
        if (!tab || !this.actionsDesktop) return;

        const viewAllLink = this.actionsDesktop.querySelector('[data-action="view-all"]');

        if (viewAllLink) {
          viewAllLink.href = tab.dataset.viewAllUrl || '#';
          viewAllLink.innerHTML = `<span>${tab.dataset.viewAllText || 'View All'}</span><svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" data-acsb-hidden="true" role="presentation"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.7563 8.13128C10.4146 8.47299 10.4146 9.02701 10.7563 9.36872L15.3876 14L10.7563 18.6313C10.4146 18.973 10.4146 19.527 10.7563 19.8687C11.098 20.2104 11.652 20.2104 11.9937 19.8687L17.8624 14L11.9937 8.13128C11.652 7.78957 11.098 7.78957 10.7563 8.13128Z" fill="#EF3340"></path></svg>`;
          viewAllLink.style.display = tab.dataset.viewAllUrl ? '' : 'none';
        }
      }

      activateProductSwiper(index, reset) {
        const swiperEl = this.productSwiperEls[index];
        if (!swiperEl) return;

        const onReady = () => {
          this.pendingProductSwipers.delete(swiperEl);
          this.setupProductSwiper(swiperEl);
          if (reset) swiperEl.swiper.slideTo(0, 0);
        };

        if (swiperEl.swiper) {
          onReady();
          return;
        }

        if (this.pendingProductSwipers.has(swiperEl)) return;
        this.pendingProductSwipers.add(swiperEl);
        swiperEl.addEventListener('swiper:ready', onReady, { once: true });
        swiperEl.setAttribute('open', 'true');
      }

      setupProductSwiper(swiperEl) {
        if (this.readyProductSwipers.has(swiperEl)) return;
        this.readyProductSwipers.add(swiperEl);

        const swiper = swiperEl.swiper;
        const fill = swiperEl.querySelector('.suunto-products-card__progress-fill');
        const prevBtn = swiperEl.querySelector('.suunto-products-card__nav-prev');
        const nextBtn = swiperEl.querySelector('.suunto-products-card__nav-next');
        const mediaEl = swiperEl.querySelector('.suunto-products-card__media');
        const swiperWrap = swiperEl.querySelector('.suunto-products-card__swiper-wrap');
        let lastMediaHeight = 0;

        const getSlidesPerPage = () => {
          const rows = swiper.params.grid && swiper.params.grid.fill === 'row' ? swiper.params.grid.rows : 1;
          const perView = typeof swiper.params.slidesPerView === 'number' ? swiper.params.slidesPerView : 1;
          return perView * rows;
        };

        const updateNavHeight = () => {
          if (!mediaEl || !swiperWrap || (!prevBtn && !nextBtn)) return;

          const mediaHeight = mediaEl.offsetHeight;
          if (!mediaHeight || mediaHeight === lastMediaHeight) return;

          lastMediaHeight = mediaHeight;
          swiperWrap.style.setProperty('--products-card-media-height', `${mediaHeight}px`);
          swiperWrap.classList.add('is-nav-ready');
        };

        const updateProgress = () => {
          const perPage = getSlidesPerPage();

          if (fill) {
            const total = swiper.slides.length;
            const pages = Math.ceil(total / perPage);
            const progress = Math.min(Math.max(swiper.progress || 0, 0), 1);
            fill.style.width = total <= perPage ? '100%' : `${100 / pages}%`;
            fill.style.transform = total <= perPage ? 'translateX(0%)' : `translateX(${progress * (pages - 1) * 100}%)`;
          }

          if (prevBtn) {
            prevBtn.disabled = swiper.isBeginning;
            prevBtn.classList.toggle('swiper-button-disabled', swiper.isBeginning);
          }
          if (nextBtn) {
            nextBtn.disabled = swiper.isEnd;
            nextBtn.classList.toggle('swiper-button-disabled', swiper.isEnd);
          }
        };

        let frame;
        // 拖动过程中合并进度更新，避免 setTranslate 连续触发布局工作。
        const queueProgressUpdate = () => {
          if (frame) return;
          frame = requestAnimationFrame(() => {
            frame = null;
            updateProgress();
          });
        };

        let navFrame;
        const queueNavHeightUpdate = () => {
          if (navFrame) return;
          navFrame = requestAnimationFrame(() => {
            navFrame = null;
            updateNavHeight();
          });
        };

        prevBtn?.addEventListener('click', () => swiper.slidePrev());
        nextBtn?.addEventListener('click', () => swiper.slideNext());

        ['progress', 'slideChange', 'reachEnd', 'fromEdge', 'setTranslate'].forEach((eventName) => {
          swiper.on(eventName, queueProgressUpdate);
        });
        ['resize', 'breakpoint'].forEach((eventName) => swiper.on(eventName, queueProgressUpdate));

        // 隐藏的 Tab 初始尺寸可能为 0，在图片区域实际变化后再定位箭头。
        if ('ResizeObserver' in window && mediaEl) {
          new ResizeObserver(queueNavHeightUpdate).observe(mediaEl);
        }

        queueNavHeightUpdate();
        updateProgress();
      }

      isMobile() {
        return this.mobileQuery?.matches ?? window.matchMedia('(max-width: 768px)').matches;
      }

      initSheet() {
        this.sheet = this.querySelector('.suunto-products-card__sheet');
        if (!this.sheet) return;

        this.sheet.querySelector('[data-sheet-close]')?.addEventListener('click', () => {
          this.closeSheet();
        });

        this.mobileQuery = window.matchMedia('(max-width: 768px)');
        this.addEventListener(
          'click',
          (event) => {
            if (!this.isMobile()) return;
            if (!(event.target instanceof Element)) return;

            const wrap = event.target.closest('.suunto-products-card__item-wrap');
            if (!wrap || !this.contains(wrap)) return;

            event.preventDefault();
            this.openSheet(wrap);
          },
          true
        );

        this.onEscape = (event) => {
          if (event.key === 'Escape') this.closeSheet();
        };

        this.onViewportChange = (mq) => {
          if (!mq.matches) this.closeSheet();
        };
        this.mobileQuery.addEventListener('change', this.onViewportChange);

        document.documentElement.addEventListener('cart:refresh', () => {
          this.closeSheet();
        });
      }

      openSheet(wrap) {
        if (!this.sheet || !wrap) return;

        const media = this.sheet.querySelector('[data-sheet-media]');
        if (media) {
          media.innerHTML = '';
          const img = wrap.querySelector('.suunto-products-card__media img, .suunto-products-card__media svg');
          if (img) {
            const clone = img.cloneNode(true);
            if (clone.tagName === 'IMG' && clone.dataset.src) {
              if (clone.dataset.srcset) {
                clone.srcset = clone.dataset.srcset;
                clone.sizes = clone.dataset.sizes;
              }
              clone.src = clone.dataset.src;
            }
            media.appendChild(clone);
          }
        }

        const titleEl = this.sheet.querySelector('[data-sheet-title]');
        if (titleEl) {
          titleEl.textContent = wrap.querySelector('.suunto-products-card__item-title')?.textContent.trim() || '';
        }

        const descSource = wrap.querySelector('.suunto-products-card__item-desc');
        const descEl = this.sheet.querySelector('[data-sheet-desc]');
        if (descEl) {
          if (descSource) {
            descEl.textContent = descSource.textContent.trim();
            descEl.hidden = false;
          } else {
            descEl.textContent = '';
            descEl.hidden = true;
          }
        }

        const priceEl = this.sheet.querySelector('[data-sheet-price]');
        const priceSource = wrap.querySelector('.suunto-products-card__item-price');
        if (priceEl) {
          priceEl.innerHTML = priceSource ? priceSource.innerHTML : '';
        }

        const cartBtn = this.sheet.querySelector('[data-sheet-cart]');
        if (cartBtn) {
          cartBtn.dataset.variantId = wrap.dataset.variantId || '';
          if (wrap.dataset.variantAvailable === 'true') {
            cartBtn.removeAttribute('disabled');
          } else {
            cartBtn.setAttribute('disabled', '');
          }
        }

        const plpEl = this.sheet.querySelector('[data-sheet-plp]');
        if (plpEl) {
          const plpText = wrap.dataset.plpText || '';
          const plpUrl = wrap.dataset.plpUrl || '';
          plpEl.textContent = plpText;
          if (plpText && plpUrl) {
            plpEl.href = plpUrl;
            plpEl.hidden = false;
          } else {
            plpEl.hidden = true;
            plpEl.removeAttribute('href');
          }
        }

        const buyEl = this.sheet.querySelector('[data-sheet-buy]');
        if (buyEl) {
          buyEl.href = wrap.dataset.productUrl || '#';
        }

        this.sheet.classList.add('is-open');
        this.sheet.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onEscape);
      }

      closeSheet() {
        if (!this.sheet || !this.sheet.classList.contains('is-open')) return;

        this.sheet.classList.remove('is-open');
        this.sheet.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.onEscape);
      }
    }
  );
}
